import { Suspense, useEffect, useState, type ComponentType } from "react";
import { QueryClientProvider } from "@tanstack/react-query";
import { queryClient } from "@/services/client";
import { HANDSHAKE_TIMEOUT_MS } from "@/services/handshake";
import { useEmbedAuth } from "@/services/auth";
import type { RendererProps } from "@/renderers/types";
import type { DocumentRoute } from "./route";
import { ErrorState, Loading } from "./states";

interface AppProps {
  route: DocumentRoute | null;
  /** Lazy renderer created in main.tsx from the eagerly-started chunk import. */
  Renderer: ComponentType<RendererProps> | null;
}

/**
 * True once the handshake has waited too long for the host's `auth` reply
 * (parent never sends a key). Resets nothing: if a valid key still arrives
 * later, the auth check below wins and the viewer recovers.
 */
function useHandshakeTimeout(waiting: boolean): boolean {
  const [timedOut, setTimedOut] = useState(false);
  useEffect(() => {
    if (!waiting) return;
    const timer = setTimeout(() => setTimedOut(true), HANDSHAKE_TIMEOUT_MS);
    return () => clearTimeout(timer);
  }, [waiting]);
  return timedOut;
}

export function App({ route, Renderer }: AppProps) {
  const auth = useEmbedAuth();
  const timedOut = useHandshakeTimeout(!auth);

  if (!route || !Renderer) return <ErrorState kind="unknown-type" />;

  if (!auth) {
    return timedOut ? <ErrorState kind="handshake-timeout" /> : <Loading />;
  }

  return (
    <QueryClientProvider client={queryClient}>
      <Suspense fallback={<Loading />}>
        <Renderer documentId={route.id} />
      </Suspense>
    </QueryClientProvider>
  );
}
